import { useState } from 'react';
import {
  AlignItems,
  Body2,
  Box,
  ButtonVariant,
  Display,
  Heading2,
  JustifyContent,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalFooter,
  ModalHeader,
  Overflow,
  useModal,
  useTheme,
} from '@gousto-internal/citrus-react';
import { CookingInstructionsModalProps } from './types';

const STEP_INSTRUCTION_MIN_HEIGHT: [string, null, string] = ['220px', null, '260px'];
const STEP_INSTRUCTION_MAX_HEIGHT: [string, null, string] = ['400px', null, '480px'];

export function CookingInstructionsModal({
  name,
  returnToModalName,
  recipeName,
  steps,
}: CookingInstructionsModalProps) {
  const { theme } = useTheme();
  const heading4Size = theme.tokens.typographyDefaults.h4.size;
  const { openModal } = useModal();
  const [stepIndex, setStepIndex] = useState(0);

  const currentStep = steps[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex >= steps.length - 1;

  return (
    <Modal
      name={name}
      testId="cooking-instructions-modal"
      aria={{ labelledby: 'cooking-instructions-modal-name' }}
    >
      <ModalHeader>
        <Heading2 size={heading4Size} id="cooking-instructions-modal-name">
          {recipeName}
        </Heading2>
        <ModalClose onClose={() => openModal(returnToModalName)} />
      </ModalHeader>
      <ModalBody paddingV={[0]} paddingH={[0]}>
        {currentStep?.imageUrl && (
          <Box height={['200px', null, '315px']}>
            <img
              src={currentStep.imageUrl}
              alt={`Step ${currentStep.number}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        )}
        <Box
          minHeight={STEP_INSTRUCTION_MIN_HEIGHT}
          maxHeight={STEP_INSTRUCTION_MAX_HEIGHT}
          overflowY={Overflow.Scroll}
          display={currentStep?.imageUrl ? undefined : Display.Flex}
          alignItems={currentStep?.imageUrl ? undefined : AlignItems.Center}
          paddingV={[4, null, 6]}
          paddingH={[4, null, 6]}
        >
          {currentStep && (
            <Body2 data-testid="cooking-instructions-step-instruction">
              <span dangerouslySetInnerHTML={{ __html: currentStep.instruction }} />
            </Body2>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box
          width="100%"
          display={Display.Flex}
          justifyContent={JustifyContent.SpaceBetween}
          alignItems={AlignItems.Center}
        >
          <ModalButton
            variant={ButtonVariant.Icon}
            icon="chevron_large_left"
            aria-label="Previous step"
            disabled={isFirstStep}
            onClick={() => setStepIndex((index) => index - 1)}
          />
          <Body2 data-testid="cooking-instructions-step-count">
            {`Step ${stepIndex + 1} of ${steps.length}`}
          </Body2>
          <ModalButton
            variant={ButtonVariant.Icon}
            icon="chevron_large_right"
            aria-label="Next step"
            disabled={isLastStep}
            onClick={() => setStepIndex((index) => index + 1)}
          />
        </Box>
      </ModalFooter>
    </Modal>
  );
}
